<template>
  <div
    v-if="work.visible"
    class="tw-rounded-md tw-border tw-border-neutral-200 tw-p-4"
  >
    <div class="tw-relative tw-flex tw-justify-between">
      <div
        class="tw-flex tw-flex-col tw-space-y-4 md:tw-flex-row md:tw-space-x-4 md:tw-space-y-0"
      >
        <div
          v-if="work.company.logoPath"
          class="tw-grid tw-shrink-0 md:tw-place-items-center"
        >
          <img
            :src="work.company.logoPath"
            alt=""
            loading="lazy"
            class="tw-h-auto tw-w-20 tw-rounded-md"
          />
        </div>
        <div>
          <h4 class="tw-heading-4">{{ work.company.name }}</h4>
          <div class="tw-text-neutral-500">{{ work.position }}</div>
          <div class="tw--mt-px tw-space-x-1 tw-text-xs tw-text-neutral-400">
            <span>
              {{ dateRange }}
            </span>
            <template v-if="work.company.website">
              <span>•</span>
              <a
                :href="websiteLink"
                target="_blank"
                class="hover:tw-text-neutral-500"
              >
                {{
                  work.company.website
                    .replace('https://', '')
                    .replace('http://', '')
                    .replace('www.', '')
                }}
              </a>
            </template>
          </div>
        </div>
      </div>

      <div
        v-if="isEditModeEnabled"
        class="tw-absolute tw-right-0 tw-top-0 tw-flex tw-items-center tw-space-x-2 md:tw-relative"
      >
        <IconButton
          color="outline-secondary"
          @click="openModal(EDIT_MODAL_NAME)"
        >
          <PenIcon />
        </IconButton>
        <IconButton color="outline-red" @click="openModal(DELETE_MODAL_NAME)">
          <BinIcon />
        </IconButton>
      </div>
    </div>
    <!--The prettier-ignore is only for strange whitespace formatting.-->
    <!-- prettier-ignore -->
    <p class="tw-text-neutral-600 tw-mt-2 tw-whitespace-pre-line tw-text-sm tw-text-break">{{ work.description }}</p>
    <div v-if="work.company.industryList" class="tw-mt-3">
      <Tag
        v-for="(industry, index) in work.company.industryList"
        :key="index"
        size="small"
        class="tw-mb-1 tw-mr-1"
      >
        {{ industry }}
      </Tag>
    </div>
  </div>
  <div
    v-else
    class="tw-relative tw-flex tw-justify-between tw-rounded-md tw-border tw-border-dashed tw-border-neutral-200 tw-p-4"
  >
    <div>
      <h4 class="tw-heading-4">{{ work.company.name }}</h4>
      <p class="tw-mt-2 tw-text-sm tw-text-error-500">
        Please fill in the details of your work experience.
      </p>
    </div>
    <div
      v-if="isEditModeEnabled"
      class="tw-absolute tw-right-2 tw-top-2 tw-flex tw-items-center tw-space-x-2 md:tw-relative md:tw-right-0 md:tw-top-0"
    >
      <IconButton color="outline-secondary" @click="openModal(EDIT_MODAL_NAME)">
        <PenIcon />
      </IconButton>
      <IconButton color="outline-red" @click="openModal(DELETE_MODAL_NAME)">
        <BinIcon />
      </IconButton>
    </div>
  </div>

  <template v-if="isEditModeEnabled">
    <ConfirmationModal :name="DELETE_MODAL_NAME" @confirmation="deleteWork" />
    <ExperienceModal :name="EDIT_MODAL_NAME" mode="EDIT" :work="work" />
  </template>
</template>

<script setup>
import IconButton from '@/shared/components/IconButton.vue'
import Tag from '@/shared/components/Tag.vue'
import ExperienceModal from './ExperienceModal.vue'
import { getUniqueId } from '@/shared/utils/uniqueId.js'
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal.vue'
import PenIcon from '@/assets/images/icons/pen.svg'
import BinIcon from '@/assets/images/icons/bin.svg'
import { useDateManager } from '@/shared/composables/useDateManager.js'
import { computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { useEditProfileModeInjector } from '@/shared/composables/edit-profile-mode/useEditProfileModeInjector.js'
import { useModals } from '@/shared/composables/useModals.js'

const props = defineProps({
  work: {
    type: Object,
    required: true,
  },
})

const { isEditModeEnabled } = useEditProfileModeInjector()
const { formatDate } = useDateManager()
const { openModal } = useModals()

const EDIT_MODAL_NAME = `growthmentor-experience-modal-${getUniqueId()}`
const DELETE_MODAL_NAME = `growthmentor-experience-confirmation-${getUniqueId()}`

const dateRange = computed(() => {
  const dateFrom = formatDate(props.work.from, 'MMMM yyyy')
  const dateTo = props.work.toPresent
    ? 'present'
    : formatDate(props.work.to, 'MMMM yyyy')
  return `${dateFrom} - ${dateTo}`
})

const websiteLink = computed(() => {
  if (props.work.company.website.startsWith('http'))
    return props.work.company.website
  return `https://${props.work.company.website}`
})

const deleteWork = () => {
  router.delete(`/profile/mentor/work_experiences/${props.work.id}`, {
    preserveScroll: true,
  })
}
</script>
